import { ReactElement } from "react";
import { Box, SxProps, Theme, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DetailedRound } from "../../../models/round";
import { formatStringCurrency } from "../../../common/CurrencyFormatter";
import { cardSx, CORRECT, CORRECT_SOFT, LINE, sectionTitleSx, supportiveSx } from "./thegamecommon";

const prizeRowSx: SxProps<Theme> = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  alignItems: "center",
  padding: "10px 0",
  fontSize: 14,
  borderTop: `1px solid ${LINE}`,
};

interface TheGamePrizePlanProps {
  round: DetailedRound;
  numLegs: number;
  // Player's best row right now; that prize level is highlighted.
  best?: number;
}

export default function TheGamePrizePlan({ round, numLegs, best }: TheGamePrizePlanProps): ReactElement | null {
  const { t } = useTranslation();
  const prizeGroups = round.operatorPrizePlan?.payoutGroups ?? [];
  if (prizeGroups.length === 0) {
    return null;
  }
  const fallbackCurrency = round.operatorStakePerRow.currency;

  return (
    <Box component="section" sx={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "4px" }}>
      <Typography component="h2" sx={sectionTitleSx}>{t("prize")}</Typography>
      <Box sx={{ ...cardSx, padding: "6px 16px" } as SxProps<Theme>}>
        <Box sx={{ ...prizeRowSx, ...supportiveSx, borderTop: "none" } as SxProps<Theme>}>
          <span>{t("correct_rows")}</span>
          <span>{t("winners")}</span>
          <Box component="span" sx={{ textAlign: "right" }}>{t("prize")}</Box>
        </Box>
        {prizeGroups.map((group, i) => {
          const numCorrect = numLegs - i;
          const isMine = numCorrect === best;
          const rows = group.rows ? Number(group.rows) : undefined;
          return (
            <Box
              key={i}
              sx={{
                ...prizeRowSx,
                fontWeight: 600,
                ...(isMine && { background: CORRECT_SOFT, color: CORRECT, borderRadius: "6px", borderTopColor: "transparent", margin: "0 -8px", padding: "10px 8px" }),
              } as SxProps<Theme>}
            >
              <span>{numCorrect}/{numLegs}</span>
              <span>{rows !== undefined ? `${rows < 1 ? Math.ceil(rows) : Math.round(rows)} ${t("st")}` : "–"}</span>
              <Box component="span" sx={{ textAlign: "right", fontWeight: 700 }}>
                {formatStringCurrency(group.payoutPerRow?.money?.amount ?? 0, group.payoutPerRow?.money?.currency ?? fallbackCurrency, t, true)}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
